import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Switch from 'react-switch';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { useTheme } from '../../../../../ThemeContext';
import RadioButtonIcon from '../../../../theme/assets/icons/RadioButtonSelected';
import BackwardArrow from '../../../../theme/assets/icons/arrow_backward.svg';
import RadioUnSelect from '../../../../theme/assets/icons/radio_button_unselect.svg';
import Arrow from '../../../../theme/assets/icons/right_arrow.svg';

import styles from './Settings.module.scss';

type Props = {
  panelClassName?: string;
  panelHeaderClassName?: string;
};

type SettingsOption = 'settings' | 'video_quality' | 'language';

const videoQualityVariants = [
  {
    key: 'Auto',
    subtext: '(Auto adjust up to 1080p)',
    value: 'auto',
  },
  {
    key: 'Full HD',
    subtext: '(Adjust up to 1080p)',
    value: '1080p',
  },
  {
    key: 'High ',
    subtext: '(Upto to 720p)',
    value: '720p',
  },
  {
    key: 'Standard',
    subtext: '(Upto to 480p)',
    value: '480p',
  },
  {
    key: 'Data saver',
    subtext: '(360p)',
    value: '360',
  },
  {
    key: 'Low',
    subtext: '(240p)',
    value: '240',
  },
];

const Settings = ({ panelClassName, panelHeaderClassName }: Props): JSX.Element => {
  const { universalStyles } = useTheme();

  const [checked, setChecked] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<SettingsOption>('settings');
  const { t, i18n } = useTranslation('common');
  const { supportedLanguages } = useConfigStore(({ supportedLanguages }) => ({ supportedLanguages }));
  const currentLanguage = useMemo(() => supportedLanguages.find(({ code }) => code === i18n.language), [i18n.language, supportedLanguages]);
  const { isVideoQualityEnabled, isAutoPlay, isMultiLanguageSupport } = useAppConfig();

  useEffect(() => {
    const storedAutoplay = localStorage.getItem('autoplay') === 'true';
    setChecked(storedAutoplay);
  }, []);

  const handleChange = () => {
    const nextChecked = !checked;
    setChecked(nextChecked);
    localStorage.setItem('autoplay', String(nextChecked));
  };

  const SettingsOptions = () => {
    return (
      <div className={panelHeaderClassName}>
        <h2>{t('Settings')}</h2>

        <div className={styles.settingsContainer}>
          {isAutoPlay && (
            <div className={styles.settingsPanel}>
              <h3 className={styles.leftText}>{t('Autoplay Next Video')}</h3>
              <span>
                <Switch
                  onChange={handleChange}
                  checked={checked}
                  height={18}
                  width={38}
                  onColor={universalStyles.signUpButton.backgroundColor}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
              </span>
            </div>
          )}

          {isVideoQualityEnabled && (
            <div className={styles.settingsPanel}>
              <div>
                <h3 className={styles.leftText}>{t('Video Quality')}</h3>
                <p className={styles.leftTextSubtitle} style={{ color: universalStyles.carouselPaginationInactive.backgroundColor }}>
                  {videoQualityVariants.find((variant) => variant.value === localStorage.getItem('videoQuality'))?.key || 'Auto'}
                </p>
              </div>
              <span className={styles.arrowIcon} onClick={() => setSelectedOptions('video_quality')}>
                <img src={Arrow} className={styles.icon} alt="Right Arrow" />
              </span>
            </div>
          )}
          {isMultiLanguageSupport && (
            <div className={styles.settingsPanel}>
              <div>
                <h3 className={styles.leftText}>{t('Language')}</h3>
                <p className={styles.leftTextSubtitle} style={{ color: universalStyles.carouselPaginationInactive.backgroundColor }}>
                  {currentLanguage ? currentLanguage.displayName : t('Select Language')}
                </p>
              </div>
              <span className={styles.arrowIcon} onClick={() => setSelectedOptions('language')}>
                <img src={Arrow} className={styles.icon} alt="Right Arrow" />
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const SettingsVideoQuality = () => {
    const [selectedQuality, setSelectedQuality] = useState<string>(localStorage.getItem('videoQuality') || 'auto');

    return (
      <div className={panelHeaderClassName}>
        <div className={styles.VDpanel}>
          <span onClick={() => setSelectedOptions('settings')} className={styles.backArrowIcon}>
            <img src={BackwardArrow} />
          </span>

          <h2 className={styles.title}>{t('Video Quality')}</h2>
        </div>

        <div className={styles.VDContainer}>
          {videoQualityVariants.map((variant) => (
            <div
              key={variant.value}
              className={styles.VDpanel}
              onClick={() => {
                localStorage.setItem('videoQuality', variant.value);
                setSelectedQuality(variant.value);
              }}
            >
              <span className={styles.arrowIcon}>
                {selectedQuality === variant.value ? <RadioButtonIcon fillColor={universalStyles.signUpButton.backgroundColor} /> : <img src={RadioUnSelect} />}
              </span>
              <div className={styles.qualityTextContainer}>
                <p className={styles.qualitySubtitle}>{variant.key}</p>
                <p className={styles.qualityText} style={{ color: universalStyles.carouselPaginationInactive.backgroundColor }}>
                  {variant.subtext}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const LanguageSettings = () => {
    const languageClickHandler = async (code: string) => {
      await i18n.changeLanguage(code);
    };
    return (
      <div className={panelHeaderClassName}>
        <div className={styles.VDpanel}>
          <span onClick={() => setSelectedOptions('settings')} className={styles.arrowIcon}>
            <img src={BackwardArrow} />
          </span>

          <h2 className={styles.title}>{t('Language')}</h2>
        </div>

        <div className={styles.VDContainer}>
          {supportedLanguages.map(({ code, displayName }) => {
            const isActive = currentLanguage?.code === code;

            return (
              <div key={code} className={styles.VDpanel} onClick={() => languageClickHandler(code)}>
                <span className={styles.arrowIcon}>
                  {isActive ? <RadioButtonIcon fillColor={universalStyles.signUpButton.backgroundColor} /> : <img src={RadioUnSelect} />}
                </span>
                <div className={styles.qualityTextContainer}>
                  <p className={styles.qualitySubtitle}>{displayName}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <section className={panelClassName}>
      {selectedOptions === 'settings' && <SettingsOptions />}
      {selectedOptions === 'video_quality' && <SettingsVideoQuality />}
      {selectedOptions === 'language' && <LanguageSettings />}
    </section>
  );
};

export default Settings;
